/**
 * Supervisão · Agora (spec 071). Lê de novo a cada 15 s e a cada volta à aba
 * (D6). O tempo corre no relógio do SERVIDOR (generatedAt), corrigido pelo
 * instante em que a resposta chegou, para um relógio errado no computador do
 * supervisor não inventar espera. A hora da última leitura fica à vista: tela
 * parada não pode parecer tela ao vivo.
 */
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock3, Headphones, Inbox, Users } from 'lucide-react';
import {
  ATTENDANCE_NOW_REFRESH_MS,
  ATTENDANT_STATUS_LABELS,
  type AttendanceNowAgent,
  type AttendanceNowChat,
} from '@erp/shared';
import { Badge, Spinner } from '@/components/ui/misc';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { attendanceNowQuery } from './supervision-queries';
import {
  LEVEL_DOT,
  LEVEL_TEXT,
  formatSeconds,
  formatWhen,
  secondsSince,
  waitLevel,
} from './supervision-format';
import { InfoTip, StatTile, TH, Section } from './supervision-ui';

const TICK_MS = 5_000;

function useTick(): number {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);
  return Date.now();
}

export function SupervisionNow({ deskIds }: { deskIds: number[] | undefined }) {
  const q = useQuery({
    ...attendanceNowQuery(deskIds),
    // o dado operacional diz a sua cadência: não herda o staleTime global (verificacao.md)
    staleTime: 0,
    refetchInterval: ATTENDANCE_NOW_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always',
  });
  const clientNow = useTick();

  if (q.isPending) {
    return (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-busy>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }
  if (q.isError) {
    return (
      <p className="rounded-lg border bg-card p-6 text-center text-sm text-destructive">
        Não foi possível carregar o atendimento. Tente de novo.
      </p>
    );
  }

  const data = q.data;
  // agora no relógio do servidor: o generatedAt mais o tempo que passou desde que a resposta chegou
  const now = new Date(data.generatedAt).getTime() + (clientNow - data.receivedAt);
  const oldestQueue = data.queue[0] ? secondsSince(data.queue[0].enteredAt, now) : null;
  const unanswered = data.inService.filter(
    (c) => waitLevel(secondsSince(c.awaitingSince, now)) !== 'ok'
  );
  const available = data.agents.filter((a) => a.status === 'available').length;
  const paused = data.agents.filter((a) => a.status === 'paused').length;
  const stuck = data.agents.filter((a) => a.status === 'away' && a.chats > 0);
  const t = data.today;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          icon={Inbox}
          label="Na fila"
          value={data.queue.length}
          tone={data.queue.length > 0 ? LEVEL_TEXT[waitLevel(oldestQueue)] : undefined}
          detail={
            data.queue.length > 0
              ? `a mais antiga espera há ${formatSeconds(oldestQueue)}`
              : 'ninguém esperando'
          }
          info="Conversas abertas sem ninguém da equipe com elas, com o cliente esperando. As que ainda estão com o bot não contam; a iniciada pela equipe entra quando o cliente responde."
        />
        <StatTile
          icon={Headphones}
          label="Em atendimento"
          value={data.inService.length}
          tone={
            unanswered.length > 0
              ? LEVEL_TEXT[waitLevel(secondsSince(unanswered[0]!.awaitingSince, now))]
              : undefined
          }
          detail={
            unanswered.length > 0
              ? `${unanswered.length} sem resposta há mais de 5 min`
              : data.inService.length > 0
                ? 'todas respondidas há pouco'
                : 'nenhuma conversa com a equipe'
          }
          info="Conversas com alguém da equipe. 'Sem resposta' conta desde a mensagem do cliente mais antiga que ainda não teve resposta."
        />
        <StatTile
          icon={Users}
          label="Equipe"
          value={available}
          valueSuffix={available === 1 ? 'disponível' : 'disponíveis'}
          tone={stuck.length > 0 ? LEVEL_TEXT.alert : undefined}
          detail={
            stuck.length > 0
              ? `${stuck.length} ausente${stuck.length > 1 ? 's' : ''} com conversa na mão`
              : `${paused} em pausa`
          }
          info="Pelo status de cada um (Disponível, Em pausa, Ausente). Ausente com conversa na mão é a conversa que pode ficar presa."
        />
        <StatTile
          icon={CheckCircle2}
          label="Hoje"
          value={t.closed}
          valueSuffix={t.closed === 1 ? 'encerrada' : 'encerradas'}
          detail={`1ª resposta média ${formatSeconds(t.firstResponse.avgSeconds)} · ${t.closedWithoutAttendance} sem atendimento`}
          info="Desde a meia-noite. 1ª resposta: da entrada na fila até a primeira mensagem de alguém da equipe. Sem atendimento: encerradas sem que ninguém da equipe as assumisse ou encerrasse."
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section
          title="Na fila"
          count={data.queue.length}
          empty="Ninguém esperando na fila."
          hint="A mais antiga primeiro"
        >
          {data.queue.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted">
                  <th className={TH}>Contato</th>
                  <th className={TH}>Mesa</th>
                  <th className={cn(TH, 'text-right')}>Esperando</th>
                </tr>
              </thead>
              <tbody>
                {data.queue.map((c) => (
                  <ChatRow key={c.chatId} chat={c} now={now} waitFrom={c.enteredAt} />
                ))}
              </tbody>
            </table>
          ) : null}
        </Section>

        <Section
          title="Equipe"
          count={data.agents.length}
          empty="Ninguém da equipe com status aberto agora."
          hint="Status agora"
        >
          {data.agents.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted">
                  <th className={TH}>Pessoa</th>
                  <th className={TH}>Status</th>
                  <th className={cn(TH, 'text-right')}>Conversas</th>
                  <th className={cn(TH, 'text-right')}>
                    Esperando
                    <InfoTip>
                      Das conversas da pessoa, o cliente sem resposta há mais tempo.
                    </InfoTip>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.agents.map((a) => (
                  <AgentRow key={a.userId} agent={a} now={now} />
                ))}
              </tbody>
            </table>
          ) : null}
        </Section>
      </div>

      <Section
        title="Em atendimento"
        count={data.inService.length}
        empty="Nenhuma conversa com a equipe agora."
        hint="Sem resposta há mais tempo primeiro"
      >
        {data.inService.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted">
                <th className={TH}>Contato</th>
                <th className={TH}>Mesa</th>
                <th className={TH}>Com</th>
                <th className={cn(TH, 'text-right')}>Há</th>
                <th className={cn(TH, 'text-right')}>Sem resposta</th>
              </tr>
            </thead>
            <tbody>
              {data.inService.map((c) => (
                <ChatRow key={c.chatId} chat={c} now={now} waitFrom={c.awaitingSince} withAgent />
              ))}
            </tbody>
          </table>
        ) : null}
      </Section>

      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {q.isFetching ? <Spinner className="size-3" /> : <Clock3 className="size-3" aria-hidden />}
        <span>
          Atualizado às {new Date(data.generatedAt).toLocaleTimeString('pt-BR')} · atualiza sozinho
          a cada {ATTENDANCE_NOW_REFRESH_MS / 1000} s
        </span>
        {data.withBot > 0 ? (
          <span>
            · {data.withBot} {data.withBot === 1 ? 'conversa' : 'conversas'} com o bot
          </span>
        ) : null}
        {q.isError || q.failureCount > 0 ? (
          <span className="flex items-center gap-1 text-[#CF1322]">
            <AlertTriangle className="size-3" aria-hidden /> a última leitura falhou; os números
            podem estar velhos
          </span>
        ) : null}
      </p>
    </div>
  );
}

function ChatRow({
  chat,
  now,
  waitFrom,
  withAgent = false,
}: {
  chat: AttendanceNowChat;
  now: number;
  waitFrom: Date | string | null;
  withAgent?: boolean;
}) {
  const wait = secondsSince(waitFrom, now);
  const level = waitLevel(wait);
  return (
    <tr className="h-10 border-b border-divider last:border-0 hover:bg-accent/60">
      <td className="max-w-56 px-4">
        <Link
          to="/chats"
          search={{ chat: chat.chatId }}
          className="block truncate font-medium text-primary hover:underline"
          title={
            chat.clientName ? `${chat.requestorName} · ${chat.clientName}` : chat.requestorName
          }
        >
          {chat.requestorName}
        </Link>
        {chat.clientName ? (
          <span className="block truncate text-xs text-muted-foreground">{chat.clientName}</span>
        ) : null}
      </td>
      <td className="max-w-40 truncate px-4 text-muted-foreground">{chat.deskName ?? '—'}</td>
      {withAgent ? (
        <>
          <td className="max-w-40 truncate px-4">{chat.responsibleName ?? '—'}</td>
          <td className="px-4 text-right whitespace-nowrap text-muted-foreground tabular-nums">
            {formatSeconds(secondsSince(chat.assumedAt, now))}
          </td>
        </>
      ) : null}
      <td className="px-4 text-right whitespace-nowrap tabular-nums">
        {wait === null ? (
          <span className="text-muted-foreground">respondida</span>
        ) : (
          <span className={cn('inline-flex items-center gap-1.5 font-medium', LEVEL_TEXT[level])}>
            <span className={cn('size-2 rounded-full', LEVEL_DOT[level])} aria-hidden />
            {formatSeconds(wait)}
          </span>
        )}
      </td>
    </tr>
  );
}

function AgentRow({ agent, now }: { agent: AttendanceNowAgent; now: number }) {
  const stuck = agent.status === 'away' && agent.chats > 0;
  const wait = secondsSince(agent.oldestAwaitingSince, now);
  const level = waitLevel(wait);
  const variant = stuck
    ? 'destructive'
    : agent.status === 'available'
      ? 'success'
      : agent.status === 'paused'
        ? 'warning'
        : 'default';
  return (
    <tr className="h-10 border-b border-divider last:border-0 hover:bg-accent/60">
      <td className="max-w-48 truncate px-4 font-medium" title={agent.userName}>
        {agent.userName}
      </td>
      <td className="px-4 py-1.5">
        <Badge variant={variant}>
          {ATTENDANT_STATUS_LABELS[agent.status]}
          {agent.reasonName ? ` · ${agent.reasonName}` : ''}
        </Badge>
        {agent.since ? (
          <span
            className="block text-xs text-muted-foreground"
            title={formatWhen(agent.since, now)}
          >
            há {formatSeconds(secondsSince(agent.since, now))}
          </span>
        ) : null}
      </td>
      <td className="px-4 text-right tabular-nums">{agent.chats}</td>
      <td
        className={cn(
          'px-4 text-right whitespace-nowrap tabular-nums',
          wait === null ? 'text-muted-foreground' : LEVEL_TEXT[level]
        )}
      >
        {wait === null ? '—' : formatSeconds(wait)}
      </td>
    </tr>
  );
}
